import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { Fragment } from 'react/jsx-runtime'
import { useFormContext } from 'react-hook-form'

import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { AffectedStaffProperties } from '../../../lib/types/models/affectedStaff/properties.affected-staff'
import { Center } from '../../../lib/types/models/center/center'
import { StaffRequest } from '../../../lib/types/models/staff-request/staff-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { getReadableDuration } from '../../../utils/functions/format-date'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import { constructCenterPath } from '../../../views/centers/utils'
import CountryFlag from '../../elements/country-flag'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import LabelWithText from '../ols/label-with-text'

type Props = {
  staffRequest: StaffRequest
}

const AffectedStaffJourneyInformation = ({ staffRequest }: Props) => {
  const { currentLanguage } = useTranslate()
  const form = useFormContext<AffectedStaffInput>()

  // the staff request, may be present and have an affected staff (over affected) and is
  // not assigned to a center when we have a deleted staff request with an affected staff on it.
  const center = staffRequest.centers?.[0] as Center | undefined
  const startDate = form.watch('startDate')
  const endDate = form.watch('endDate')

  const readableDuration =
    startDate && endDate
      ? getReadableDuration(new Date(startDate), new Date(endDate), {
          includeStartAndEndDays: true
        })
      : '-'

  return (
    <InnerBox title="Informations sur le séjour" className="rounded-lg">
      <div className=" grid grid-cols-2 items-center gap-2">
        <LabelWithText
          label="Centre"
          data={
            <Typography variant="tag" color={tailwindColors.blue.DEFAULT} className="underline">
              {staffRequest.centers.map((center, index) => (
                <Fragment key={center.id}>
                  <TextLink
                    name={center.name}
                    href={constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id)}
                  />
                  {index < staffRequest.centers.length - 1 && ', '}
                </Fragment>
              ))}
            </Typography>
          }
        />
        <LabelWithText
          label="N° tel LEC du responsable du centre"
          data={valueRender(center?.mobileNumberForFrenchLeader?.phoneNumber, (value) =>
            formatPhoneNumber(value)
          )}
        />
        <LabelWithText
          label="Pays"
          data={
            staffRequest.country ? (
              <CountryFlag
                possibleCountry={staffRequest.country?.name}
                code={staffRequest.country?.code}
              />
            ) : (
              '-'
            )
          }
        />
        <LabelWithText
          label="Formule"
          className="text-wrap"
          data={
            staffRequest.formulas && staffRequest.formulas.length > 0
              ? staffRequest.formulas.map((formula) => getTranslatedName(formula)).join(', ')
              : '-'
          }
        />
        <LabelWithText
          label="Option obligatoire"
          data={AffectedStaffProperties.getOptions(staffRequest.centers, currentLanguage)}
        />
      </div>
      <div className="h-3" />

      <LabelWithText
        label="Dates du séjour"
        data={
          <div className="flex gap-1 items-center">
            <Badge
              text={
                startDate && endDate
                  ? `${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`
                  : '-'
              }
            />
            <Badge text={readableDuration} />
          </div>
        }
      />
      <div className="h-4" />
      <FormField
        control={form.control}
        name="facNumber"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ControlledPhoneInput
                {...field}
                value={field.value ?? undefined}
                label="N° LEC pour le séjour"
                placeholder="N° LEC pour le séjour"
                defaultCountry="FR"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </InnerBox>
  )
}

export default AffectedStaffJourneyInformation
